import {present} from './lib/utils'

type Selector<T, R> = (value: T) => R

/**
 * Dereference a prop followed by a path of optional selectors.
 * This function is useful if you don't know if the prop you have
 * is a window.prop (function) or just a plain value.
 *
 * example:
 *     const purchaseOrder = prop({reference: prop('P123')});
 *     deref(purchaseOrder, (po) => po.reference)
 *     // returns: 'P123'
 *
 *     // the following inputs give the same answer:
 *     // purchaseOrder = window.prop({reference: 'P123'})
 *     // purchaseOrder = {reference: window.prop('P123')}
 *     // purchaseOrder = {reference: 'P123'}
 */
export function deref<T>(prop: T | (() => T)): T
export function deref(prop: unknown, ...selectors: Selector<unknown, unknown>[]): unknown
export function deref(prop: unknown, ...selectors: Selector<unknown, unknown>[]): unknown {
    if (selectors.length > 0) {
        const [fn, ...rest] = selectors
        return deref(present(fn, 'selector')(deref(prop)), ...rest)
    }

    if (isFunction(prop)) {
        return prop()
    }

    return prop
}

/**
 * Checks if a value is a function
 */
export function isFunction(value: unknown): value is (...args: never[]) => unknown {
    return typeof value === 'function'
}

/**
 * Capitalize the first letter of all words in a string.
 */
export function titleize(str: string): string {
    return str.replaceAll(/\b[a-z]/g, (letter) => letter.toUpperCase())
}

/**
 * Remove diacritics from a string.
 */
export function remove_diacritics(str: string): string {
    return str.normalize('NFD').replaceAll(/[\u0300-\u036F]/g, '')
}
